import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { Box, CircularProgress, Pagination } from "@mui/material";
import dayjs from "dayjs";

import { useTime } from "@/components/Application/TimeContext";

import SearchBar from "@/helpers/components/SearchBar";
import SortMenu from "@/helpers/components/SortMenu/SortMenu";

type Props<T> = {
	items: T[];
	itemsPerPage: number;
	children: (items: T[]) => ReactNode;
	getItemName: (item: T) => string;
	getItemDate: (item: T) => Date | string | undefined;
	initialSortOrder?: "ASC" | "DESC";
	isLoading?: boolean;
	emptyState?: ReactNode;
	serverSidePagination?: {
		currentPage: number;
		totalPages: number;
		onPageChange: (page: number) => void;
		onSearchChange: (search: string) => void;
		onSortChange: (sort: "ASC" | "DESC") => void;
	};
};

const PaginatedSearchWrapper = <T,>({
	items,
	itemsPerPage = 10,
	initialSortOrder = "ASC",
	children,
	getItemName,
	getItemDate,
	isLoading = false,
	emptyState = null,
	serverSidePagination,
}: Props<T>) => {
	const { timezone } = useTime();
	const [searchParams, setSearchParams] = useState<string>("");
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">(initialSortOrder);

	const isServerSide = !!serverSidePagination;

	const onSearchChangeRef = useRef(serverSidePagination?.onSearchChange);
	onSearchChangeRef.current = serverSidePagination?.onSearchChange;

	useEffect(() => {
		if (onSearchChangeRef.current) {
			onSearchChangeRef.current(searchParams);
		}
	}, [searchParams]);

	const filteredSorted = useMemo(() => {
		const filtered = isServerSide
			? items
			: (() => {
					const needle = searchParams.trim().toLowerCase();
					return items.filter((item) =>
						getItemName(item)?.toLowerCase().includes(needle),
					);
				})();

		if (isServerSide) {
			return filtered;
		}

		const sorted = filtered.sort((a, b) => {
			const dateA = dayjs(getItemDate(a)).tz(timezone);
			const dateB = dayjs(getItemDate(b)).tz(timezone);

			if (sortOrder === "ASC") return dateA.isAfter(dateB) ? 1 : -1;

			return dateA.isBefore(dateB) ? 1 : -1;
		});

		return sorted;
	}, [
		items,
		searchParams,
		sortOrder,
		timezone,
		getItemName,
		getItemDate,
		isServerSide,
	]);

	const effectivePage = isServerSide
		? serverSidePagination.currentPage
		: currentPage;
	const effectiveTotalPages = isServerSide
		? serverSidePagination.totalPages
		: Math.max(1, Math.ceil(filteredSorted.length / itemsPerPage));
	const current = Math.min(effectivePage, effectiveTotalPages);

	const paginatedItems = useMemo(() => {
		if (isServerSide) {
			return filteredSorted; // Server already returns the correct page
		}
		return filteredSorted.slice(
			(current - 1) * itemsPerPage,
			current * itemsPerPage,
		);
	}, [filteredSorted, current, itemsPerPage, isServerSide]);

	const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
		if (isServerSide) {
			serverSidePagination.onPageChange(page);
		} else {
			setCurrentPage(page);
		}
	};

	const handleSortChange = (newSort: "ASC" | "DESC") => {
		setSortOrder(newSort);
		if (isServerSide) {
			serverSidePagination!.onSortChange!(newSort);
		}
	};

	if (isLoading) {
		return (
			<Box>
				<Box display="flex" justifyContent="end" gap={2} mb={1.5}>
					<SearchBar
						searchParams={searchParams}
						setSearchParams={setSearchParams}
						disabled
					/>
					<SortMenu
						sortOrder={sortOrder}
						setSortOrder={handleSortChange}
						disabled
					/>
				</Box>
				<Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			<Box display="flex" justifyContent="end" gap={2} mb={1.5}>
				<SearchBar
					searchParams={searchParams}
					setSearchParams={setSearchParams}
				/>
				<SortMenu sortOrder={sortOrder} setSortOrder={handleSortChange} />
			</Box>

			{filteredSorted.length === 0 ? (
				(emptyState ?? null)
			) : (
				<>
					{children(paginatedItems)}
					<Pagination
						sx={{ display: "flex", justifyContent: "end", mt: 1.5 }}
						count={effectiveTotalPages}
						page={current}
						onChange={handlePageChange}
					/>
				</>
			)}
		</Box>
	);
};

export default PaginatedSearchWrapper;
